import { gql, useLazyQuery, useMutation } from '@apollo/react-hooks';
import { Formik } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { Header, Modal, Table } from '../../components';
import { Search } from '../../components/Header/components';
import { Block, Button, SelectInput, Text, TextInput } from '../../components/layout';
import ButtonAction from '../../components/Table/components/ButtonAction';
import { SvgIconEdit, SvgIconModalDelete, SvgIconTrash } from '../../svgs';
import HasScope from '../../utils/HasScope';
import { store } from '../../utils/store';
import validateFormSchema from '../../utils/validateFormSchema';

function Pages() {
    const { language } = useContext(store);
    const history = useHistory();
    const paginate = useRef(false);
    const [search, setSearch] = useState("");
    const [modalCreateIsOpen, setModalCreateIsOpen] = useState(false);
    const [modalDeleteIsOpen, setModalDeleteIsOpen] = useState(null);

    const [result, setResult] = useState({ edges: [], pageInfo: { hasNextPage: false }, totalCount: 0 });
    const [templates, setTemplates] = useState([]);

    const [queryPages, { loading }] = useLazyQuery(
        gql`
            query ($first: Int, $after: String, $search: String) {
                pages(first: $first, after: $after, search: $search) {
                    edges {
                        node {
                            id
                            slug
                            title
                            status
                        }
                        cursor
                    }
                    pageInfo {
                        startCursor
                        endCursor
                        hasNextPage
                    }
                    totalCount
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.pages) {
                    setResult({
                        edges: paginate.current ? [...result.edges, ...data.pages.edges] : data.pages.edges,
                        pageInfo: data.pages.pageInfo,
                        totalCount: data.pages.totalCount
                    });
                    paginate.current = false;
                }
            }
        }
    );

    const [mutationPageCreate, { loading: loadingPageCreate }] = useMutation(
        gql`
            mutation pageCreate($slug: String!, $template: ID!) {
                pageCreate(slug: $slug, template: $template) {
                    id
                    slug
                    title
                    status
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.pageCreate) {
                    history.push(`/editor/page/${data.pageCreate.id}`);
                }
            }
        }
    );

    const [mutationPageDelete, { loading: loadingPageDelete }] = useMutation(
        gql`
            mutation pageDelete($id: ID!) {
                pageDelete(id: $id) {
                    id
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.pageDelete) {
                    const index = result.edges.findIndex((edge) => edge.cursor == data.pageDelete.id);
                    if (index !== -1) {
                        result.edges.splice(index, 1);
                        setModalDeleteIsOpen(null);
                        setResult([...data]);
                    }
                }
            }
        }
    );

    const [queryTemplates, { loading: loadingTemplates }] = useLazyQuery(
        gql`
            query {
                templates(first: 1000) {
                    edges {
                        node {
                            id
                            name
                        }
                        cursor
                    }
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.templates) {
                    setTemplates(data.templates.edges.map((edge) => ({ value: edge.cursor, label: edge.node.name })));
                }
            }
        }
    );

    const requestData = (params = { next: false }) => {
        const { first, after, next } = params;

        if (next) {
            paginate.current = true;
        }

        queryPages({
            variables: {
                first: first || 20,
                after: after || undefined,
                search: search !== "" ? search : undefined
            }
        });
    }

    useEffect(() => {
        requestData();
        queryTemplates();
    }, [language]);

    useEffect(() => {
        requestData();
    }, [search]);

    return (
        <>
            <Block flex height="100%">
                <Header
                    leftChildren={<Search onChange={(e) => setSearch(e.target.value)} />}
                    rightChildren={<HasScope roles={["role.developer"]}><Button style={{ minWidth: 309 }} text="Add page" onClick={() => setModalCreateIsOpen(true)} /></HasScope>}
                />
                <Block height="100%" mr={20} ml={20} style={{ overflow: "hidden" }}>
                    <Table
                        loading={loading}
                        columns={[
                            { label: "Title", key: "title" },
                            { label: "Slug", key: "slug" },
                            { label: "Status", key: "status" }
                        ]}
                        rows={result.edges.map((edge) => ({ ...edge.node, actions: <><ButtonAction icon={<SvgIconEdit />} text="Edit" onClick={() => history.push(`/editor/page/${edge.cursor}`)} /><HasScope roles={["role.developer"]}><ButtonAction icon={<SvgIconTrash />} onClick={() => setModalDeleteIsOpen(edge.cursor)} /></HasScope></> }))}
                        next={() => requestData({ next: true, after: result.pageInfo.endCursor })}
                        hasMore={result.pageInfo.hasNextPage}
                    />
                </Block>
            </Block>
            <Modal
                title="Create page"
                isOpen={modalCreateIsOpen}
                onRequestClose={() => setModalCreateIsOpen(false)}
            >
                <Text size={14} height={20}>Pages</Text>
                <Block mt={28}>
                    <Formik
                        initialValues={{}}
                        validateOnChange={false}
                        validateOnBlur={false}
                        validate={values => {
                            return validateFormSchema(
                                yup.object().shape({
                                    slug: yup.string().required(),
                                    template: yup.number().required()
                                }),
                                values
                            )
                        }}
                        onSubmit={(values, { setSubmitting }) => {
                            setSubmitting(false);
                            mutationPageCreate({ variables: values });
                        }}
                    >
                        {({
                            values,
                            errors,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                        }) => {
                            return (
                                <form onSubmit={handleSubmit} autoComplete="off">
                                    <Block>
                                        <Block>
                                            <Block>
                                                <TextInput name="slug" label="Slug" type="text" onChange={handleChange} onBlur={handleBlur} value={values.slug} error={errors.slug} />
                                            </Block>
                                            <Block mt={12}>
                                                <SelectInput name="template" label="Template" type="text" onBlur={handleBlur} error={errors.template} options={templates} />
                                            </Block>
                                        </Block>
                                        <Block mt={44}>
                                            <Button type="submit" text="Submit" loading={loadingPageCreate} />
                                        </Block>
                                    </Block>
                                </form>
                            )
                        }}
                    </Formik>
                </Block>
            </Modal>
            <Modal
                width={280}
                title="Delete page"
                isOpen={!!modalDeleteIsOpen}
                onRequestClose={() => setModalDeleteIsOpen(null)}
            >
                <Block mb={20}>
                    <SvgIconModalDelete />
                </Block>
                <Text size={18} height={24}>Are you sure that you want to delete this page?</Text>
                <Text size={12} height={14} mt={8}>This action it’s irreversible, it will erase all the data from this page.</Text>
                <Block mt={32}>
                    <Button text="Delete" loading={loadingPageDelete} onClick={() => mutationPageDelete({ variables: { id: modalDeleteIsOpen } })} />
                </Block>
            </Modal>
        </>
    );
}

export default Pages;